import { ApolloClient, InMemoryCache } from "@apollo/client";
import {
  GET_ALL_POSTS,
  GET_ALL_SLUGS,
  GET_INDIVIDUAL_POST,
  GET_LAST_ARTICLES_BY_TAGS,
} from "../../graphql/posts";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import Recommendations from "../../components/ArticleRecommendation/ArticleRecommendation";
import DiscoverBeneBono from "../../components/DiscoverBeneBono/DiscoverBeneBono";
import { ArrowLeft, Comments, Email, Twitter } from "../../assets";
import Head from "next/head";
import ShareButton from "../../components/ShareButton/ShareButton";
import Navbar from "../../components/Nav/Nav";
import Footer from "../../components/Footer/Footer";
import NavHook from "../../components/Nav/NavHook";
import { useTranslation } from "next-i18next";
import { useEffect } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import RefreshedFooter from "../../components/RefreshedFooter/RefreshedFooter";
import DiscoverBlogArticles from "../../components/DiscoverBlogArticles/DiscoverBlogArticles";

const client = new ApolloClient({
  uri: process.env.STRAPI_GRAPHQL_URL,
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: "no-cache",
    },
  },
});

export default function BlogArticle({
  post,
  latestsPosts,
  navigation,
  footer,
}) {
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    window?.rudderanalytics?.page();
  }, []);

  const tags = post.tags.data.map((t) => t.attributes.name);

  return (
    <>
      <Head>
        <title>{post?.title}</title>
        <meta
          property="og:title"
          content={post?.seo?.metaTitle}
        />
        <meta
          property="og:site_name"
          content="Bene Bono"
        />
        <meta
          property="og:description"
          content={post?.seo?.metaDescription}
        />
        <meta
          property="og:type"
          content="website"
        />
        <meta
          property="og:image"
          content={post?.seo?.metaImage?.data?.attributes?.url}
        />
        {process.env.PLASMIC_ENV !== "production" && (
          <meta
            name="robots"
            content="index,follow"
          />
        )}
      </Head>
      <Navbar links={navigation.links} />
      <div>
        <div className="single_article_container">
          <div className="article_header">
            <span onClick={() => router.push("/blog")}>
              <ArrowLeft /> {t("blog.back.button")}
            </span>
            <h1>{post?.title}</h1>
            <div className="article_header_info">
              {/* <div className="duration_badge_article">
                <img src="/clock.svg" alt="Horloge" className="clock_icon_duration_article" />
                <span>5 min</span>
              </div> */}
              <div className="article_header_info_author_date">
                <p>écrit par <b>{post?.author}</b></p>
                <p>{dayjs(post?.publishedAt).format("DD MMMM YYYY")}</p>
              </div>
              {tags.length > 0 ? (
                <ul>
                  {tags.map((t, i) => (
                    <>
                      <div className="article_header_tags_container">
                        <div className="article_header_tags">
                          <p>{tags}</p>
                        </div>
                      </div>
                    </>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </div>
            {/* <div className="socials_container">
              <ShareButton
                icon={<Email />}
                text="Email"
                href="mailto:someoneemail@com"
              />
              <ShareButton
                icon={<Twitter />}
                text="Twitter"
                href="https://twitter.com/intent/tweet"
              />
            </div> */}
          </div>
          <div className="content">
            <div className="article_content">
              <ReactMarkdown
                disallowedTypes={["Paragraph"]}
                rehypePlugins={[rehypeRaw]}
              >
                {post?.content}
              </ReactMarkdown>
            </div>
            <DiscoverBeneBono posts={latestsPosts} />
          </div>
        </div>
        {/* <Recommendations
          title={t("last.article.title")}
          link="blog"
          data={latestsPosts}
          imageUrl="https://images.unsplash.com/photo-1610348725531-843dff563e2c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1770&q=80"
        /> */}
        <div className="discover_blog_articles_container_single_article">
          <DiscoverBlogArticles posts={{ data: latestsPosts }} title={t("selection.blog.desktop2")} />
        </div>
      </div>
      <RefreshedFooter links={footer.links} />
    </>
  );
}

export async function getStaticPaths({ locale }) {
  const posts = await client.query({
    query: GET_ALL_POSTS,
    variables: {
      page: 1,
      pageSize: 10,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const { data } = await client.query({
    query: GET_ALL_SLUGS,
    variables: {
      pageSize: parseInt(posts?.data.blogPosts.meta.pagination.total),
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const paths = data.blogPosts.data.map((post) => {
    return {
      params: { slug: post.attributes.slug },
    };
  });

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params, locale }) {
  const { data } = await client.query({
    query: GET_INDIVIDUAL_POST,
    variables: {
      slugUrl: params.slug,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const latestPosts = await client.query({
    query: GET_LAST_ARTICLES_BY_TAGS,
    variables: {
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const mainNav = await NavHook(locale, "main");
  const navigation = {
    links: mainNav,
  };

  const footerNav = await NavHook(locale, "footer");
  const footer = {
    links: footerNav,
  };

  if (!data || !latestPosts) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale)),
      post: data.blogPosts.data[0].attributes,
      latestsPosts: latestPosts.data.blogPosts.data,
      navigation,
      footer,
      locale,
    },
    revalidate: 15,
  };
}
